import React, { ChangeEvent, FC, Key, ReactElement } from 'react'
import { InputAdornment, TextField, Tooltip } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { AutocompleteRenderInputParams } from '@material-ui/lab'
import FormRow from '../../../../React/Common/Components/FormRow'
import { EMPTY_STRING } from '../../../../NonReact/constants/Common'
import { isStringNotEmpty, IsVarEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { ArrayUtils } from '../../../../NonReact/utils/ArrayUtils'
import { TelSysTypes } from '../../../../NonReact/constants/TelephonyServerTypes'
import { isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'

export interface SelectFilterOption {
    label: string
    value: string | number
    telSysType?: TelSysTypes
}

interface SelectFilterProps {
    options: SelectFilterOption[]
    label: string
    icon?: ReactElement
    onChange: (value: Nullable<SelectFilterOption>) => void
    disabled?: boolean
    allowClearable?: boolean
    value?: Nullable<SelectFilterOption>
    defaultValue?: Key
    alignLabel?: 'top' | 'left'
    /**
     * When {@link persistentPlaceholderTooltip.show} is true and {@link persistentPlaceholderTooltip.title}
     * is non-empty, the tooltip stays open until the placeholder condition clears (controlled open state).
     */
    persistentPlaceholderTooltip?: {
        show: boolean
        title: string
    }
}

const resolveOption = (
    option: Nullable<SelectFilterOption>,
    options: SelectFilterOption[]
): Nullable<SelectFilterOption> => {
    if (IsVarEmpty(option) || IsVarEmpty(options) || ArrayUtils.isEmpty(options)) return option
    if (isStringNotEmpty(option.label)) return option

    const resolved: Definable<SelectFilterOption> = options.find(
        (opt: SelectFilterOption): boolean => opt.value === option.value
    )

    return resolved ?? option
}

export const SelectFilter: FC<SelectFilterProps> = ({
    options,
    label,
    icon,
    onChange,
    disabled,
    allowClearable,
    value,
    defaultValue,
    alignLabel,
    persistentPlaceholderTooltip
}: SelectFilterProps): ReactElement => {
    const resolvedValue: Nullable<SelectFilterOption> =
        resolveOption(value ?? null, options ?? []) ??
        options?.find((opt: SelectFilterOption): boolean => opt.value === defaultValue) ??
        null

    const tooltipOpen: boolean =
        Boolean(persistentPlaceholderTooltip?.show) &&
        isStringNotEmpty(persistentPlaceholderTooltip?.title ?? EMPTY_STRING)

    const autocomplete: ReactElement = (
        <Autocomplete
            onChange={(_: ChangeEvent<object>, val: Nullable<SelectFilterOption>): void => onChange(val)}
            options={options}
            classes={{ popper: 'select-filter-popper' }}
            getOptionSelected={(option: SelectFilterOption, candidate: SelectFilterOption): boolean =>
                option.value === candidate.value
            }
            getOptionLabel={(option: SelectFilterOption): string =>
                isStringNotEmpty(option?.label) ? option.label : String(option?.value ?? EMPTY_STRING)
            }
            value={resolvedValue}
            disableClearable={!allowClearable}
            disabled={disabled}
            renderInput={(params: AutocompleteRenderInputParams): ReactElement => (
                <FormRow>
                    <TextField
                        {...params}
                        size='small'
                        label={label}
                        variant='outlined'
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: <InputAdornment position='start'>{icon}</InputAdornment>
                        }}
                    />
                </FormRow>
            )}
        />
    )

    if (!tooltipOpen) return autocomplete

    return (
        <Tooltip
            classes={{
                tooltip: 'select-filter-persistent-tooltip-surface',
                arrow: 'select-filter-persistent-tooltip-arrow'
            }}
            open
            placement='top'
            title={
                <span className='select-filter-persistent-tooltip'>
                    {persistentPlaceholderTooltip?.title ?? EMPTY_STRING}
                </span>
            }
            arrow
            disableFocusListener
            disableHoverListener
            disableTouchListener
        >
            <span className='select-filter-persistent-tooltip-anchor' style={{ display: 'block', width: '100%' }}>
                {autocomplete}
            </span>
        </Tooltip>
    )
}
